/**
 * /tools/virtue-reassessment?clientId=X — STP-128.
 *
 * Re-rates the previously-scored virtues (STP-028). Pulls the latest
 * `virtue-scoring` submission to seed the virtue list (with prior scores),
 * lets the client pick a new 1..10 score per virtue, and submits.
 *
 * If no prior virtue-scoring exists, surface a CTA to /tools/virtue-scoring.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface VirtueRow {
  virtueName: string;
  oldScore: number | null;
  newScore: string;
  note: string;
}

interface FormState {
  changedAtStep: number;
  virtues: VirtueRow[];
  reflection: string;
}

const DEFAULT_FORM: FormState = {
  changedAtStep: 5,
  virtues: [],
  reflection: '',
};

export function VirtueReassessmentPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [seedSource, setSeedSource] = useState<'priorScoring' | 'priorReassessment' | 'none'>('none');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const [prior, scoring] = await Promise.all([
          api.getToolCompletion(accessToken, clientId, 'virtue-reassessment'),
          api.getToolCompletion(accessToken, clientId, 'virtue-scoring'),
        ]);
        if (!active) return;
        let virtues: VirtueRow[] = [];
        let source: 'priorScoring' | 'priorReassessment' | 'none' = 'none';
        if (prior?.payload && typeof prior.payload === 'object') {
          const p = prior.payload as Record<string, unknown>;
          if (Array.isArray(p.virtues)) {
            virtues = p.virtues.map((raw) => {
              const v = raw as Record<string, unknown>;
              return {
                virtueName: typeof v.virtueName === 'string' ? v.virtueName : '',
                oldScore: null,
                newScore: typeof v.newScore === 'number' ? String(v.newScore) : '',
                note: typeof v.note === 'string' ? v.note : '',
              };
            });
            source = 'priorReassessment';
            setSavedAt(prior.completed_at ?? null);
          }
        }
        if (virtues.length === 0 && scoring?.payload && typeof scoring.payload === 'object') {
          const p = scoring.payload as Record<string, unknown>;
          if (Array.isArray(p.virtues)) {
            virtues = p.virtues
              .map((raw) => {
                const v = raw as Record<string, unknown>;
                if (typeof v.virtueName !== 'string') return null;
                return {
                  virtueName: v.virtueName,
                  oldScore: typeof v.score === 'number' ? v.score : null,
                  newScore: '',
                  note: '',
                };
              })
              .filter((row): row is VirtueRow => row !== null);
            source = 'priorScoring';
          }
        }
        setForm((prev) => ({ ...prev, virtues }));
        setSeedSource(source);
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const validRowCount = form.virtues.filter(
    (v) =>
      v.virtueName.trim().length > 0 &&
      v.newScore.trim().length > 0 &&
      Number(v.newScore) >= 1 &&
      Number(v.newScore) <= 10,
  ).length;
  const canSubmit = !submitting && hydrated && validRowCount > 0;

  const setRow = (idx: number, key: 'newScore' | 'note', value: string): void => {
    setForm((prev) => ({
      ...prev,
      virtues: prev.virtues.map((row, i) => (i === idx ? { ...row, [key]: value } : row)),
    }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.virtueReassessment.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      changedAtStep: form.changedAtStep,
      virtues: form.virtues
        .filter(
          (v) =>
            v.virtueName.trim().length > 0 &&
            v.newScore.trim().length > 0 &&
            Number(v.newScore) >= 1 &&
            Number(v.newScore) <= 10,
        )
        .map((v) => ({
          virtueName: v.virtueName.trim(),
          newScore: Number(v.newScore),
          ...(v.note.trim() ? { note: v.note.trim() } : {}),
        })),
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'virtue-reassessment',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.virtueReassessment.errors.generic'));
      } else {
        setSubmitError(t('tools.virtueReassessment.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="virtue-reassessment-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.virtueReassessment.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.virtueReassessment.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.virtueReassessment.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.virtueReassessment.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.virtueReassessment.description')}
        </p>
      </header>

      {hydrated && form.virtues.length === 0 && (
        <article
          className="rounded-lg border border-bone-3 bg-bone-2 p-s6"
          data-testid="virtue-reassessment-needs-scoring"
        >
          <h2 className="font-display text-display-md text-ink">
            {t('tools.virtueReassessment.needsScoring.title')}
          </h2>
          <p className="mt-s3 text-gray-2 max-w-prose">
            {t('tools.virtueReassessment.needsScoring.body')}
          </p>
          <Link
            to={`/carpe-diem?clientId=${encodeURIComponent(clientId)}`}
            className="btn-primary mt-s4 inline-block"
          >
            {t('tools.virtueReassessment.needsScoring.cta')}
          </Link>
        </article>
      )}

      {form.virtues.length > 0 && (
        <form
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          className="space-y-s5"
          noValidate
        >
          {seedSource === 'priorScoring' && (
            <p className="text-xs text-gray-1" data-testid="virtue-reassessment-seed-note">
              {t('tools.virtueReassessment.seededFromScoring')}
            </p>
          )}

          <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <h2 className="font-display text-display-sm text-ink">
              {t('tools.virtueReassessment.tableTitle')}
            </h2>
            <p className="mt-s2 text-gray-2 italic">
              {t('tools.virtueReassessment.tableHint')}
            </p>
            <ol
              className="mt-s4 space-y-s3"
              data-testid="virtue-reassessment-list"
            >
              {form.virtues.map((row, idx) => (
                <li
                  key={idx}
                  className="rounded-md border border-bone-3 bg-bone p-s3 space-y-s2"
                  data-testid={`virtue-reassessment-row-${idx}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-base text-ink">{row.virtueName}</span>
                    {row.oldScore !== null && (
                      <span className="font-mono text-xs uppercase tracking-wide text-gray-1">
                        {t('tools.virtueReassessment.priorScore', { score: row.oldScore })}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-12 gap-s2">
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={row.newScore}
                      onChange={(e) => setRow(idx, 'newScore', e.target.value)}
                      placeholder="1-10"
                      className="input-field col-span-3"
                      data-testid={`virtue-reassessment-score-${idx}`}
                      aria-label={t('tools.virtueReassessment.newScoreAria', { name: row.virtueName })}
                    />
                    <input
                      type="text"
                      value={row.note}
                      onChange={(e) => setRow(idx, 'note', e.target.value)}
                      placeholder={t('tools.virtueReassessment.notePlaceholder')}
                      className="input-field col-span-9"
                      data-testid={`virtue-reassessment-note-${idx}`}
                    />
                  </div>
                </li>
              ))}
            </ol>
          </article>

          <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <label className="block">
              <span className="form-label">
                {t('tools.virtueReassessment.reflection')}
                <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                  {t('tools.virtueReassessment.optional')}
                </span>
              </span>
              <textarea
                value={form.reflection}
                onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
                className="input-field mt-s2 min-h-24"
                data-testid="virtue-reassessment-reflection"
                rows={4}
              />
            </label>
          </article>

          {submitError && (
            <p role="alert" className="form-error" data-testid="virtue-reassessment-submit-error">
              {submitError}
            </p>
          )}

          {savedAt && (
            <p
              role="status"
              className="text-sm text-flame-deep"
              data-testid="virtue-reassessment-saved-at"
            >
              ✓ {t('tools.virtueReassessment.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
            </p>
          )}

          <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
            {submitting
              ? t('tools.virtueReassessment.submitting')
              : t('tools.virtueReassessment.submit')}
          </button>
          <FinalizeWarning />
        </form>
      )}
    </section>
  );
}
